// Premier League fixtures using team_stats.csv and fixtures.csv
import { useEffect, useMemo, useRef, useState } from 'react';
import GameBox from '../../components/GameBox';
import teamLogos from '../../assets/PLsoccerLogos';
import '../../css/PremierLeagueMatches.css';

const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Strip trailing " FC" / " AFC" so fixture names match CSV names
// Example: "Arsenal FC" → "Arsenal", 
const normalizeTeamName = (name) => {
  const n = name.trim();
  if (n.endsWith(' FC')) return n.slice(0, -3);
  if (n.endsWith(' AFC')) return n.slice(0, -4);
  return n;
};

const pairKeyFromName = (name) => {
  if (!name || typeof name !== 'string') return '';
  let n = name.trim().toLowerCase().replace(/['']/g, '');
  if (n.startsWith('afc ')) n = n.slice(4).trim();
  if (n.endsWith(' fc')) n = n.slice(0, -3).trim();
  if (n.endsWith(' afc')) n = n.slice(0, -4).trim();
  n = n.replace(/&/g, 'and').replace(/\s+/g, ' ').trim();

  const nicknameToFull = {
    wolves: 'wolverhampton wanderers',
    spurs: 'tottenham hotspur',
    'man utd': 'manchester united',
    'man united': 'manchester united',
    'man city': 'manchester city',
    "nott'm forest": 'nottingham forest',
    'nottm forest': 'nottingham forest',
  };
  if (nicknameToFull[n]) return nicknameToFull[n];

  n = n.replace(/\butd\b\.?/g, 'united').replace(/\s+/g, ' ').trim();
  return n;
};

const teamPairKey = (home, away) =>
  `${pairKeyFromName(home)}|${pairKeyFromName(away)}`;

const sameClubForMerge = (a, b) => {
  const x = pairKeyFromName(a);
  const y = pairKeyFromName(b);
  if (x === y) return true;
  const [shorter, longer] = x.length <= y.length ? [x, y] : [y, x];
  if (shorter.length < 6) return false;
  return longer === shorter || longer.startsWith(`${shorter} `);
};

const PL_MAX_MATCHDAY = 38;

// Map API team label to a `teamLogos` key when a week has no CSV row. 
const resolveDisplayTeamName = (raw) => {
  if (!raw || typeof raw !== 'string') return raw;
  if (teamLogos[raw]) return raw;
  const target = normalizeTeamName(raw).toLowerCase().replace(/\s+/g, ' ');
  const hit = Object.keys(teamLogos).find(
    (k) => normalizeTeamName(k).toLowerCase().replace(/\s+/g, ' ') === target
  );
  return hit || raw;
};

const formatApiMatchDateHeading = (utcDate) => {
  if (!utcDate) return 'TBD';
  try {
    return new Date(utcDate).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'TBD';
  }
};

// Calculate home-win, draw, and away-win percentages from CSV stats
const calcPrediction = (homeTeam, awayTeam, teamStats) => {
  const homeName = normalizeTeamName(homeTeam);
  const awayName = normalizeTeamName(awayTeam);

  const homeRow = teamStats[`${homeName}__home`];
  const awayRow = teamStats[`${awayName}__away`];

  if (!homeRow || !awayRow) return null;

  const hPlayed = Number(homeRow.played) || 1;
  const aPlayed = Number(awayRow.played) || 1;

  const homeWinRate = Number(homeRow.wins) / hPlayed;
  const homeDrawRate = Number(homeRow.draws) / hPlayed;
  const homeLossRate = Number(homeRow.losses) / hPlayed;

  const awayWinRate = Number(awayRow.wins) / aPlayed;
  const awayDrawRate = Number(awayRow.draws) / aPlayed;
  const awayLossRate = Number(awayRow.losses) / aPlayed;

  // Average each outcome from both teams' perspective, then normalize
  let pHome = (homeWinRate + awayLossRate) / 2;
  let pDraw = (homeDrawRate + awayDrawRate) / 2;
  let pAway = (homeLossRate + awayWinRate) / 2;

  const total = pHome + pDraw + pAway;
  pHome = Math.round((pHome / total) * 100);
  pDraw = Math.round((pDraw / total) * 100);
  pAway = 100 - pHome - pDraw;

  return { home: pHome, draw: pDraw, away: pAway };
};

// Render a form string like "WLDWL" as colored badges
const FormBadges = ({ form }) => (
  <div className="form-badges">
    {form.split('').map((ch, i) => (
      <span key={i} className={`form-badge form-${ch}`}>{ch}</span>
    ))}
  </div>
);

// Full match prediction block: probability bar, teams (HOME/AWAY), comparison table, form 
const MatchPredictionPanel = ({ homeTeam, awayTeam, teamStats, pred }) => {
  const homeName = normalizeTeamName(homeTeam);
  const awayName = normalizeTeamName(awayTeam);

  const homeAll = teamStats[`${homeName}__all`] || {};
  const homeHome = teamStats[`${homeName}__home`] || {};
  const awayAll = teamStats[`${awayName}__all`] || {};
  const awayAway = teamStats[`${awayName}__away`] || {};

  const homeHeader = (homeAll.shortName || homeName || '—').toUpperCase();
  const awayHeader = (awayAll.shortName || awayName || '—').toUpperCase();

  return (
    <div className="match-prediction-panel">
      <h4 className="match-prediction-title">G&C Prediction</h4>

      {pred ? (
        <div className="prediction-section">
          <div className="pred-bar">
            <div className="pred-segment pred-home" style={{ width: `${pred.home}%` }}>
              {pred.home}%
            </div>
            <div className="pred-segment pred-draw" style={{ width: `${pred.draw}%` }}>
              {pred.draw}%
            </div>
            <div className="pred-segment pred-away" style={{ width: `${pred.away}%` }}>
              {pred.away}%
            </div>
          </div>
          <div className="pred-labels">
            <span>Home Win</span>
            <span>Draw</span>
            <span>Away Win</span>
          </div>
        </div>
      ) : (
        <p className="stats-model-unavailable">
          No home/away stats found for this fixture (check team names vs <code>team_stats.csv</code>).
        </p>
      )}

      <div className="modal-teams match-prediction-teams">
        <div className="modal-team">
          {teamLogos[homeTeam] && (
            <img src={teamLogos[homeTeam]} alt={homeTeam} className="modal-logo" />
          )}
          <span className="modal-team-name">{homeTeam}</span>
          <span className="modal-team-tag">HOME</span>
        </div>
        <span className="modal-vs">VS</span>
        <div className="modal-team">
          {teamLogos[awayTeam] && (
            <img src={teamLogos[awayTeam]} alt={awayTeam} className="modal-logo" />
          )}
          <span className="modal-team-name">{awayTeam}</span>
          <span className="modal-team-tag">AWAY</span>
        </div>
      </div>

      <table className="stats-table match-prediction-stats">
        <thead>
          <tr>
            <th>{homeHeader}</th>
            <th>STAT</th>
            <th>{awayHeader}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{homeAll.idx || '—'}</td>
            <td>League Position</td>
            <td>{awayAll.idx || '—'}</td>
          </tr>
          <tr>
            <td>{homeAll.pts || '—'}</td>
            <td>Total Points</td>
            <td>{awayAll.pts || '—'}</td>
          </tr>
          <tr>
            <td>{`${homeHome.wins || '0'}-${homeHome.draws || '0'}-${homeHome.losses || '0'}`}</td>
            <td>Record (H / A)</td>
            <td>{`${awayAway.wins || '0'}-${awayAway.draws || '0'}-${awayAway.losses || '0'}`}</td>
          </tr>
          <tr>
            <td>{homeHome.scoresStr || '—'}</td>
            <td>Goals (H / A)</td>
            <td>{awayAway.scoresStr || '—'}</td>
          </tr>
          <tr>
            <td>{homeHome.pts || '—'}</td>
            <td>Points (H / A)</td>
            <td>{awayAway.pts || '—'}</td>
          </tr>
          <tr>
            <td><FormBadges form={homeAll.form_last5 || ''} /></td>
            <td>Form (Last 5)</td>
            <td><FormBadges form={awayAll.form_last5 || ''} /></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};


const PremierLeagueMatches = () => {
  const [allMatches, setAllMatches] = useState([]);
  const [selectedmatchweek, setSelectedmatchweek] = useState('');
  const [predictions, setPredictions] = useState({});
  const [teamStats, setTeamStats] = useState({});
  const [seasonInfo, setSeasonInfo] = useState({
    loaded: false,
    currentMatchday: null,
    rateLimited: false,
  });
  const [apiWeekState, setApiWeekState] = useState({
    loading: false,
    matches: [],
    skipped: false,
    rateLimited: false,
    error: null,
  });
  const didPickInitialWeek = useRef(false);

  // Parses fixtures into a match list and matchweek options
  useEffect(() => {
    const url = process.env.PUBLIC_URL + '/data/fixtures.csv';

    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        const rows = lines.slice(1);

        const clean = (s) => s.replace(/\r/g, '').replace(/^"|"$/g, '').trim();
        const parsed = rows.map((row, index) => {
          const parts = row.split(',');
          const matchweek = clean(parts[0]);
          const home = clean(parts[1]);
          const away = clean(parts[2]);
          const date = clean(parts[3] || 'TBD');
          return {
            id: `${home}-${away}-${date}`,
            matchweek,
            homeTeam: home,
            awayTeam: away,
            date,
          };
        });

        setAllMatches(parsed);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiBase}/api/soccer/premier-league/season`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setSeasonInfo({
          loaded: true,
          currentMatchday: data?.currentMatchday ?? null,
          rateLimited: !!data?.rateLimited,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSeasonInfo({ loaded: true, currentMatchday: null, rateLimited: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matchweekOptions = useMemo(() => {
    const csvNums = allMatches
      .map((m) => Number(m.matchweek))
      .filter((n) => !Number.isNaN(n));
    const csvMax = csvNums.length ? Math.max(...csvNums) : 0;
    const cm =
      seasonInfo.currentMatchday != null ? Number(seasonInfo.currentMatchday) : 0;
    if (!seasonInfo.loaded && csvMax === 0) return [];
    const high = Math.min(PL_MAX_MATCHDAY, Math.max(1, csvMax, cm));
    return Array.from({ length: high }, (_, i) => String(i + 1));
  }, [allMatches, seasonInfo.currentMatchday, seasonInfo.loaded]);

  useEffect(() => {
    if (
      didPickInitialWeek.current ||
      !allMatches.length ||
      !seasonInfo.loaded ||
      !matchweekOptions.length
    ) {
      return;
    }
    didPickInitialWeek.current = true;
    const cm = seasonInfo.currentMatchday;
    let pick;
    if (cm != null) {
      const n = Number(cm);
      const eligible = matchweekOptions.filter((w) => Number(w) <= n);
      pick = eligible.length
        ? eligible[eligible.length - 1]
        : matchweekOptions[matchweekOptions.length - 1];
    } else {
      const csvNums = allMatches
        .map((m) => Number(m.matchweek))
        .filter((x) => !Number.isNaN(x));
      pick = csvNums.length
        ? String(Math.max(...csvNums))
        : matchweekOptions[matchweekOptions.length - 1];
    }
    setSelectedmatchweek(String(pick));
  }, [matchweekOptions, seasonInfo.loaded, seasonInfo.currentMatchday, allMatches]);

  // Load team_stats.csv in client/public/data/team_stats.csv
  // Has a lookup keyed by "name__filter", e.g. "Arsenal__home", "Arsenal__away"
  useEffect(() => {
    const url = process.env.PUBLIC_URL + '/data/team_stats.csv';

    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        const headers = lines[0].split(',');
        const rows = lines.slice(1);

        const lookup = {};
        rows.forEach((row) => {
          const parts = row.split(',');
          const obj = {};
          headers.forEach((h, i) => { obj[h] = parts[i]; });

          const key = `${obj.name}__${obj.filter}`;
          lookup[key] = obj;
        });
        setTeamStats(lookup);
      });
  }, []);

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/predictions`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include"
        });

        if (!res.ok) {
          console.error("Failed to fetch predictions:");
          return;
        }

        const data = await res.json();
        const formatted = {};
        data.forEach(pred => {
          formatted[pred.matchId] = pred.selection;
        });
        setPredictions(formatted);
      } catch (err) {
        console.error("Error fetching predictions:", err);
      }
    };
    fetchPredictions();
  }, []);

  useEffect(() => {
    if (!selectedmatchweek || !seasonInfo.loaded) return;

    let cancelled = false;
    setApiWeekState((s) => ({
      ...s,
      loading: true,
      error: null,
      skipped: false,
    }));

    fetch(
      `${apiBase}/api/soccer/premier-league/matches?matchday=${encodeURIComponent(selectedmatchweek)}`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data?.rateLimited) {
          setApiWeekState({
            loading: false,
            matches: [],
            skipped: false,
            rateLimited: true,
            error: null,
          });
          return;
        }
        setApiWeekState({
          loading: false,
          matches: Array.isArray(data?.matches) ? data.matches : [],
          skipped: false,
          rateLimited: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setApiWeekState({
          loading: false,
          matches: [],
          skipped: false,
          rateLimited: false,
          error: err?.message || 'Request failed',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [selectedmatchweek, seasonInfo.loaded]);

  const handlePredictionChange = async (matchId, selection) => {
    setPredictions((prev) => ({ ...prev, [matchId]: selection }));

    await fetch(`${process.env.REACT_APP_API_URL}/api/predictions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ matchId, selection })
    });
  };

  const filteredMatches = allMatches.filter((m) => m.matchweek === selectedmatchweek);

  const apiByPair = useMemo(() => {
    const map = new Map();
    apiWeekState.matches.forEach((row) => {
      if (!row?.homeTeam || !row?.awayTeam) return;
      map.set(teamPairKey(row.homeTeam, row.awayTeam), row);
    });
    return map;
  }, [apiWeekState.matches]);

  const filteredMatchesWithApi = useMemo(() => {
    const apiList = apiWeekState.matches || [];
    if (filteredMatches.length) {
      return filteredMatches.map((row) => {
        let apiResult = apiByPair.get(teamPairKey(row.homeTeam, row.awayTeam)) || null;
        if (!apiResult) {
          apiResult =
            apiList.find(
              (m) =>
                m?.homeTeam &&
                m?.awayTeam &&
                sameClubForMerge(row.homeTeam, m.homeTeam) &&
                sameClubForMerge(row.awayTeam, m.awayTeam)
            ) || null;
        }
        return { ...row, apiResult };
      });
    }
    if (!apiList.length) return [];
    return apiList.map((api) => ({
      id: `api-${api.apiMatchId}`,
      matchweek: selectedmatchweek,
      homeTeam: resolveDisplayTeamName(api.homeTeam),
      awayTeam: resolveDisplayTeamName(api.awayTeam),
      date: formatApiMatchDateHeading(api.utcDate),
      apiResult: api,
    }));
  }, [filteredMatches, apiByPair, apiWeekState.matches, selectedmatchweek]);

  // Computes the % for each fixture (CSV + API-only weeks)
  const statsModelByMatchId = useMemo(() => {
    const out = {};
    allMatches.forEach((m) => {
      out[m.id] = calcPrediction(m.homeTeam, m.awayTeam, teamStats);
    });
    filteredMatchesWithApi.forEach((m) => {
      if (out[m.id] === undefined) {
        out[m.id] = calcPrediction(m.homeTeam, m.awayTeam, teamStats);
      }
    });
    return out;
  }, [allMatches, teamStats, filteredMatchesWithApi]);

  // Group current week’s matches under their date heading
  const matchesByDate = useMemo(() => {
    const out = {};
    filteredMatchesWithApi.forEach((match) => {
      const key = match.date || 'TBD';
      if (!out[key]) out[key] = [];
      out[key].push(match);
    });
    return out;
  }, [filteredMatchesWithApi]);

  return (
    <main>

      <div id="premier-league-content">
        <div className="matchweek-selector">
          <select
            id="matchweek-select"
            value={selectedmatchweek}
            onChange={(e) => setSelectedmatchweek(e.target.value)}
          >
            {!matchweekOptions.includes(selectedmatchweek) && (
              <option value="" disabled>Loading…</option>
            )}
            {matchweekOptions.map((md) => (
              <option key={md} value={md}>Matchweek {md}</option>
            ))}
          </select>
        </div>

        {apiWeekState.loading && (
          <p className="pl-week-api-hint">Loading match results…</p>
        )}
        {apiWeekState.rateLimited && (
          <p className="pl-week-api-hint pl-week-api-hint--warn">
            Match results are temporarily unavailable (API rate limit). Try again shortly.
          </p>
        )}
        {apiWeekState.error && (
          <p className="pl-week-api-hint pl-week-api-hint--warn">
            Could not load scores: {apiWeekState.error}
          </p>
        )}
        {seasonInfo.rateLimited && seasonInfo.loaded && (
          <p className="pl-week-api-hint pl-week-api-hint--warn">
            Season info unavailable (rate limit); default matchweek may be off.
          </p>
        )}

        {Object.entries(matchesByDate).map(([date, matches]) => (
          <div key={date} className="date-group">
            <h3 className="match-date">{date}</h3>
            {matches.map((match) => {
              const api = match.apiResult;
              const hasFullTimeScore =
                api?.fullTime?.home != null &&
                api?.fullTime?.away != null &&
                !Number.isNaN(Number(api.fullTime.home)) &&
                !Number.isNaN(Number(api.fullTime.away));
              const fullTime = hasFullTimeScore ? api.fullTime : null;

              return (
              <details key={match.id} className="match-tab">
                <summary className="match-tab-summary">
                  <div className="match-tab-summary-inner">
                    <span className="match-tab-chevron" aria-hidden />
                    <GameBox
                      homeTeam={match.homeTeam}
                      awayTeam={match.awayTeam}
                      fullTime={fullTime}
                    />
                  </div>
                </summary>
                <div className="match-tab-panel">
                  <div className="match-tab-panel-split">
                    <div className="match-tab-panel-graphic">
                      <MatchPredictionPanel
                        homeTeam={match.homeTeam}
                        awayTeam={match.awayTeam}
                        teamStats={teamStats}
                        pred={statsModelByMatchId[match.id]}
                      />
                    </div>
                    <aside className="match-tab-panel-predict" aria-label="Your prediction">
                      <div className="prediction-buttons prediction-buttons--aside">
                        <p className="prediction-label">Your Prediction:</p>
                        <div className="button-row">
                          <button
                            type="button"
                            className={`prediction-button ${predictions[match.id] === 'home' ? 'selected' : ''}`}
                            onClick={() => handlePredictionChange(String(match.id), 'home')}
                          >
                            {match.homeTeam}
                          </button>
                          <button
                            type="button"
                            className={`prediction-button ${predictions[match.id] === 'draw' ? 'selected' : ''}`}
                            onClick={() => handlePredictionChange(String(match.id), 'draw')}
                          >
                            Draw
                          </button>
                          <button
                            type="button"
                            className={`prediction-button ${predictions[match.id] === 'away' ? 'selected' : ''}`}
                            onClick={() => handlePredictionChange(String(match.id), 'away')}
                          >
                            {match.awayTeam}
                          </button>
                        </div>
                      </div>
                    </aside>
                  </div>
                </div>
              </details>
            );
            })}
          </div>
        ))}
      </div>
    </main>
  );
};
export default PremierLeagueMatches;